import React, { useState } from 'react';
import { User, ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';
import type { Profile } from '../../types/database';

interface MusicianSearchSelectProps {
  musicians: Profile[];
  selectedMusician: Profile | null;
  onSelectMusician: (musician: Profile | null) => void;
  placeholder?: string;
  error?: string;
  loading?: boolean;
}

export function MusicianSearchSelect({ 
  musicians, 
  selectedMusician, 
  onSelectMusician, 
  placeholder = 'Search musicians...',
  error,
  loading = false
}: MusicianSearchSelectProps) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(searchQuery, 300);

  // Filter musicians based on search query
  const filteredMusicians = musicians.filter(musician => 
    !debouncedSearch || 
    musician.artist_name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    musician.full_name?.toLowerCase().includes(debouncedSearch.toLowerCase()) ||
    musician.location?.toLowerCase().includes(debouncedSearch.toLowerCase())
  );

  const handleMusicianSelect = (musician: Profile) => {
    onSelectMusician(musician);
    setShowDropdown(false);
    setSearchQuery('');
  };

  const handleClear = () => {
    onSelectMusician(null);
    setShowDropdown(false);
    setSearchQuery('');
  };

  return (
    <div 
      className="relative"
      onBlur={(e) => {
        if (!e.relatedTarget?.closest('.musician-search-dropdown')) {
          setShowDropdown(false);
        }
      }}
    >
      <div className="w-full">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          disabled={loading}
          className={`w-full px-4 py-2 bg-dark border rounded-lg text-light flex items-center justify-between hover:border-gray-custom/40 transition-colors disabled:opacity-50 ${
            error ? 'border-red-500/40' : 'border-gray-custom/20'
          }`}
        >
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-light/40" />
            <span className="truncate">
              {selectedMusician 
                ? (selectedMusician.artist_name || selectedMusician.full_name)
                : placeholder
              }
            </span>
          </div>
          <ChevronDown className={`w-5 h-5 text-light/40 transition-transform ${showDropdown ? 'transform rotate-180' : ''}`} />
        </button>

        {showDropdown && (
          <div className="musician-search-dropdown absolute z-[100] w-full mt-1 bg-[#232323] border border-gray-custom/20 rounded-lg shadow-lg">
            <div className="p-3 border-b border-gray-custom/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-light/40" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('bands.inviteMember.searchPlaceholder')}
                  className="w-full pl-9 pr-3 py-2 bg-dark border border-gray-custom/20 rounded-lg text-light placeholder-light/40 focus:outline-none focus:border-gray-custom/40"
                  autoFocus
                />
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto">
              {selectedMusician && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="w-full px-4 py-3 text-left hover:bg-gray-custom/10 text-light/60 border-b border-gray-custom/10"
                >
                  <span className="italic">{t('common.clearAll')}</span>
                </button>
              )}
              
              {filteredMusicians.map((musician) => (
                <button
                  key={musician.id}
                  type="button"
                  onClick={() => handleMusicianSelect(musician)}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-custom/10 text-light/80 flex items-center space-x-3 ${
                    selectedMusician?.id === musician.id ? 'bg-light/10' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {musician.avatar_url ? (
                      <img
                        src={musician.avatar_url}
                        alt={musician.artist_name || ''}
                        className="w-full h-full object-cover"
                       loading="lazy" />
                    ) : (
                      <User className="w-4 h-4 text-light/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">
                      {musician.artist_name || musician.full_name}
                    </div>
                    {musician.full_name && musician.artist_name !== musician.full_name && (
                      <div className="text-sm text-light/60 truncate">{musician.full_name}</div>
                    )}
                    {musician.location && (
                      <div className="text-sm text-light/60 truncate">{musician.location}</div>
                    )}
                    {musician.skills && musician.skills.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-1">
                        {musician.skills.slice(0, 3).map((skill) => (
                          <span
                            key={skill}
                            className="px-1.5 py-0.5 bg-secondary rounded text-xs text-light"
                          >
                            {skill}
                          </span>
                        ))}
                        {musician.skills.length > 3 && (
                          <span className="text-xs text-light/40">
                            +{musician.skills.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              ))}
              
              {filteredMusicians.length === 0 && (
                <div className="px-4 py-6 text-light/40 text-center">
                  {debouncedSearch ? t('bands.inviteMember.noMusiciansFound') : 'No musicians available'}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}